import { collection, doc, DocumentReference } from 'firebase/firestore';
import { db } from '../google/firebase';
import { Standard, EntityLabel, EsgCategory } from '@esg/esg-global-types';
import { BatchWrite } from './db_util';
import { auth } from '../google/firebase';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { validateMasterListParams } from '../../util/validation';
import { apollo_client } from './db_util';
import {
  ALLOW_DELETE_STANDARD_QUERY,
  GET_STANDARDS_MASTER_LIST_QUERY,
  GET_STANDARDS_QUERY,
  GET_UNCONFIGURED_STANDARDS_QUERY
} from '../graphql/queries/standard';
import {
  CREATE_STANDARD_MUTATOR,
  DELETE_STANDARD_MUTATOR,
  DELETE_STANDARD_WITH_METRICS_MUTATOR,
  UPDATE_STANDARD_MUTATOR
} from '../graphql/mutations/standard';
import { ApolloQueryResult } from '@apollo/client';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';

const SOURCE = 'lib/app/standard.ts';

export type StandardData = Omit<Standard, 'id' | 'reference'>;

export interface CachedStandard {
  [key: string]: Standard;
}

// Singular and plural label for model entity.
export const standard_label: EntityLabel = {
  one: 'Standard',
  many: 'Standards'
};

/**
 * Create Firestore database reference for either configured Standard or Master List Standard.
 * @param {boolean} master_list Reference standard in master list.
 * @param {string} id ID of object to reference.
 * @param {Group} group_id ID of configured group.
 * @param {string} company_id ID of configured company.
 * @returns {DocumentReference}
 */
export const refStandard = (
  master_list: boolean,
  id: string,
  group_id?: string,
  company_id?: string
): DocumentReference =>
  doc(
    collection(
      db,
      !master_list && group_id && company_id
        ? `groups/${group_id}/companies/${company_id}/standards`
        : 'standard_master_list'
    ),
    id
  );

/**
 * Create a collection path string for the master list standards or configured standards.
 * @param {boolean} master_list Reference the master list collection.
 * @param {string} group_id When not referencing a master list collection, a group id for the configured group is required.
 * @param {string} company_id When not referencing a master list collection, a company id for the configured company is required.
 * @returns {string} Path to a standards collection.
 */
export const createStandardCollectionPath = (
  master_list: boolean,
  group_id?: string,
  company_id?: string
): string => {
  try {
    validateMasterListParams(master_list, group_id, company_id);
    return master_list
      ? `/standard_master_list`
      : `groups/${group_id}/companies/${company_id}/standards`;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createStandardCollectionPath'),
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query standard documents for specified group and company.
 * @param {Group} group_id ID of configured group.
 * @param {string} company_id ID of configured company.
 * @param {boolean} is_quantitative retrieve only quantitative or only qualitative documents.
 * @returns {Promise<Array<Standard>>}
 */
export const getStandards = async (
  group_id: string,
  company_id: string,
  is_quantitative?: boolean,
  references?: Array<string>
): Promise<Array<Standard>> => {
  try {
    const standards: Array<Standard> = await apollo_client
      .query({
        query: GET_STANDARDS_QUERY,
        variables: {
          group_id: group_id,
          company_id: company_id,
          is_quantitative: is_quantitative,
          references: references
        },
        fetchPolicy: 'no-cache'
      })
      .then((response: ApolloQueryResult<{ standards: Array<Standard> }>) => {
        return response.data.standards;
      });
    return standards;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getStandards'),
      {
        group_id: group_id,
        company_id: company_id,
        is_quantitative: is_quantitative,
        references: references
      },
      tracking_id
    );
  }
};

/**
 * Query all unconfigured standard documents for current portal company.
 * @param {Group} group_id id of current portal company.
 * @param {string} company_id id of current portal company.
 * @param {boolean} is_quantitative retrieve only quantitative or only qualitative documents.
 * @returns {Array<Standard>}
 */
export const getUnconfiguredStandards = async (
  group_id: string,
  company_id: string,
  is_quantitative?: boolean
) => {
  try {
    const unconfigured_standards: Array<Standard> = await apollo_client
      .query({
        query: GET_UNCONFIGURED_STANDARDS_QUERY,
        variables: {
          group_id: group_id,
          company_id: company_id,
          is_quantitative: is_quantitative
        },
        fetchPolicy: 'no-cache'
      })
      .then((response) => response.data.unconfigured_standards);
    return unconfigured_standards;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getUnconfiguredStandards'),
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query all standard documents from master list
 * @param {boolean} is_quantitative Query only quantitative or quantitative Standards from master list.
 * @returns {Promise<Array<Standard>>}
 */
export const getStandardsMasterList = async (
  is_quantitative?: boolean
): Promise<Array<Standard>> => {
  try {
    const standards: Array<Standard> = await apollo_client
      .query({
        query: GET_STANDARDS_MASTER_LIST_QUERY,
        variables: {
          is_quantitative: is_quantitative
        },
        fetchPolicy: 'no-cache'
      })
      .then((response) => response.data.standards_master_list);
    return standards;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getStandardsMasterList'),
      {
        is_quantitative: is_quantitative
      },
      tracking_id
    );
  }
};

/**
 * Create Standard data in firestore on master list or company level
 * @param {boolean} master_list Flag to create new Standard within master list or company level collection
 * @param {Array<Standard | StandardData>} standards Standard objects to create
 * @param {string | undefined} group_id Optional ID of Group to create standards for
 * @param {string | undefined} company_id Optional ID of Company to create standards for
 * @returns {Promise<Array<BatchWrite>>}
 */
export const createStandards = async (
  master_list: boolean,
  standards: Array<Standard | StandardData>,
  group_id?: string,
  company_id?: string
): Promise<Array<BatchWrite>> => {
  try {
    const standard_writes: Array<BatchWrite> = await apollo_client
      .mutate({
        mutation: CREATE_STANDARD_MUTATOR,
        variables: {
          master_list: master_list,
          standards: standards,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => response.data.createStandards);

    return standard_writes;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createStandards'),
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id,
        standards: standards
      },
      tracking_id
    );
  }
};

/**
 * Update standard with relative data
 * @param {boolean} master_list Flag to update standard in master list
 * @param {Standard} updated_standard New Standard data to push into document
 * @param {Standard} original_standard Old Standard data
 * @param {string} group_id Optional ID of Group to update standard for
 * @param {string} company_id Optional ID of Company to update standard for
 * @returns {void}
 */
export const updateStandard = async (
  master_list: boolean,
  updated_standard: Standard,
  original_standard: Standard,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_STANDARD_MUTATOR,
        variables: {
          master_list: master_list,
          updated_standard: updated_standard,
          original_standard: original_standard,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => response.data.updateStandard);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateStandard'),
      {
        group_id: group_id,
        company_id: company_id,
        updated_standard: updated_standard,
        original_standard: original_standard
      },
      tracking_id
    );
  }
};

/**
 * Delete company Standard
 * @param {string} group_id ID of Group to delete standard for
 * @param {string} company_id ID of Company to delete standard for
 * @param {string} standard_id ID of standard to delete
 * @returns {Promise<void>}
 */
export const deleteStandard = async (
  group_id: string,
  company_id: string,
  standard_id: string,
  standard_name: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_STANDARD_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          standard_id: standard_id,
          standard_name: standard_name,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => response.data.deleteStandard);
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteStandard'),
      {
        group_id: group_id,
        company_id: company_id,
        standard_id: standard_id,
        standard_name: standard_name
      },
      tracking_id
    );
  }
};

/**
 * Soft delete a Standard and any related Metrics and Emission Factors in Firestore transaction
 * @param {boolean} master_list Flag to delete entities from their respective master lists
 * @param {string | undefined} group_id Optional Group to delete standard from
 * @param {string | undefined} company_id Optional Company to delete standard from
 * @param {string} standard_id ID of standard to delete
 * @param {string} standard_name Name of standard being deleted
 * @return {void}
 */
export const deleteStandardWithMetrics = async (
  master_list: boolean,
  standard_id: string,
  standard_name: string,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_STANDARD_WITH_METRICS_MUTATOR,
        variables: {
          master_list: master_list,
          standard_id: standard_id,
          standard_name: standard_name,
          group_id: group_id,
          company_id: company_id
        }
      })
      .then((response) => response.data.deleteStandardWithMetrics);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteStandardWithMetrics'),
      {
        group_id: group_id,
        company_id: company_id,
        standard_id: standard_id,
        standard_name: standard_name
      },
      tracking_id
    );
  }
};

/**
 * Check if a Standard can be safely deleted
 * @param {string} group_id ID of Group to check Standard for
 * @param {string} company_id ID of Company to check Standard for
 * @param {string} standard_id ID of standard to check
 * @returns {Promise<boolean>}
 */
export const allowDeleteStandard = async (
  group_id: string,
  company_id: string,
  standard_id: string
): Promise<boolean> => {
  try {
    const allow_delete: boolean = await apollo_client
      .query({
        query: ALLOW_DELETE_STANDARD_QUERY,
        variables: {
          group_id: group_id,
          company_id: company_id,
          standard_id: standard_id
        },
        fetchPolicy: 'no-cache'
      })
      .then((response) => response.data.allowDeleteStandard);
    return allow_delete;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'allowDeleteStandard'),
      {
        standard_id: standard_id,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Get the standardized metric esg category from a given standard.
 * @param {Standard} standard Standard to parse for esg category
 * @returns {'e' | 's' | 'g'}
 */
export const getMetricCategoryFromStandard = (standard: Standard): EsgCategory => {
  try {
    const esg_categories_map: Record<string, EsgCategory> = {
      environmental: 'e',
      social: 's',
      governance: 'g'
    };
    const standard_category: string = standard.category.toLowerCase();
    if (!Object.keys(esg_categories_map).includes(standard_category)) {
      throw new Error('Standard category must be Environmental, Social or Governance for metrics');
    }
    return esg_categories_map[standard_category];
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getMetricCategoryFromStandard'),
      {
        standard: standard
      },
      tracking_id
    );
  }
};
